import { captureException } from '@sentry/react'
import { toast } from 'react-hot-toast'
import { ValidLibraryObjectActionState } from '@breakoutlearning/firebase-repository/stores/ValidLibraryObject'
import { useStudentAssignmentCubit } from 'hooks/cubits/studentAssignment'
import { useDialogs } from 'hooks/dialogs'
import { useRootStore } from 'hooks/rootStore'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect } from 'react'
import { RoomsDialog } from './dialogs/RoomsDialog'
import { QuizModal } from './QuizModal'
import { StartQuizModal } from './StudentAssignmentStep2'
import { LibraryObjectState } from '@breakoutlearning/firebase-repository/types'
import { useWalkthroughDialog } from './dialogs/WalkthroughDialog.hooks'
import { MeetingResultsDialog } from './dialogs/MeetingResultsDialog'
import { SummaryDialog } from './dialogs/SummaryDialog'
import { StudentAssignmentChatDialog } from './dialogs/GroupChatDialog'
import { useTranslationTyped } from 'i18n/i18n'
import { showScheduleRoomDatePickerDialog } from './dialogs/ScheduleRoomStateDialogUtil'
import { DatePickerDialog } from 'components/design-system/BreakoutDateTimeInput'
import { RoomStateStatus } from '@breakoutlearning/firebase-repository/models/RoomState'

/**
 * To be used on the StudentAssignmentPage
 * this component will only perform any logic on the initial render
 * and will not render anything
 *
 * This component is responsible for handling various actions defined
 * in LibraryObject as as ValidLibraryObjectActionState.
 *
 * We see if there is an action defined in the 'action' query
 * parameter, and if it is a valid action (can be parsed to the enum),
 * and if the assignment/room is an appropriate state we perform that action on behalf of the user,
 *
 * i.e joinRoom => popGroup dialog, startSession -> popStartSession dialog
 *
 */
export const StudentAssignmentLibraryActionParser = observer(
  function StudentAssignmentLibraryActionParser() {
    const { showDialog, popDialog } = useDialogs()
    const store = useRootStore()
    const cubit = useStudentAssignmentCubit()
    const { tt } = useTranslationTyped()

    const { showWalkthroughDialogIfNecessary } = useWalkthroughDialog({
      roomId: cubit.roomStateId,
      roomStateStatus: cubit.libraryObject.roomStateStatus,
    })

    const runAction = useCallback(
      (action: ValidLibraryObjectActionState | 'chat' | 'takeQuiz') => {
        // special action
        if (action === 'chat') {
          // scheduling must be enabled
          // user must be in room or corre
          // room state must be set
          if (
            !(cubit.userIsInCurrentRoom || cubit.user.isCorre) ||
            !cubit.roomStateId
          ) {
            return
          }

          setTimeout(() => {
            showDialog(() => <StudentAssignmentChatDialog cubit={cubit} />)
          }, 250) // Wait to show the chat until the page transition is complete
          return
        }

        // `takeQuiz` is the deep-link the meeting-page pre-quiz interstitial
        // (`pages/meeting/PreWorkInterstitial.tsx`) uses to route a student
        // straight into the quiz. Unlike `completeQuiz`, this skips
        // StartQuizModal — the interstitial already showed the "you can
        // only take this once / 10 mins" copy.
        if (action === 'takeQuiz') {
          if (cubit.allPreWorkQuestionsAnswered) return
          showDialog(({ remove: removeQuizModal }) => (
            <QuizModal
              cubit={cubit}
              closeModal={() => {
                removeQuizModal()
              }}
            />
          ))
          return
        }

        switch (action) {
          case ValidLibraryObjectActionState.joinGroup: {
            if (
              cubit.roomState.isEmpty &&
              !cubit.libraryObject.requiresPayment &&
              !cubit.assignment.forbidStudentGroupJoining
            ) {
              return showDialog(() => <RoomsDialog cubit={cubit} />)
            }
            return
          }
          case ValidLibraryObjectActionState.joinNewGroup: {
            const doJoinNewGroup = async () => {
              if (cubit.roomStateId) {
                await cubit.leaveRoom()
              }
              showDialog(() => <RoomsDialog cubit={cubit} />)
            }
            doJoinNewGroup()
            return
          }
          case ValidLibraryObjectActionState.scheduleSession: {
            if (
              cubit.roomState.isEmpty ||
              !cubit.currentUserCanScheduleRoom ||
              cubit.assignment.data.forbidStudentGroupScheduling
            )
              return

            return showScheduleRoomDatePickerDialog({
              cubit,
              showDatePickerDialog: (p) =>
                showDialog(() => <DatePickerDialog {...p} />),
              popDialog,
              tt,
            })
          }
          case ValidLibraryObjectActionState.completeQuiz: {
            if (cubit.allPreWorkQuestionsAnswered) return
            // solo: only act when the central mapping agrees (blocks stale
            // deep links on expired/canceled/unpaid/pending assignments,
            // where ensureSoloRoom would create a room as a side effect)
            if (
              cubit.assignment.isSolo &&
              cubit.libraryObject.getActionState({
                hasOutstandingPeerReview: false,
              }) !== ValidLibraryObjectActionState.completeQuiz
            )
              return
            return showDialog(({ remove: removeStartQuizModal }) => {
              return (
                <StartQuizModal
                  onClick={async () => {
                    // quiz answers are stored under a roomId, so a solo
                    // student's room must exist before the quiz opens
                    try {
                      await cubit.ensureSoloRoom()
                    } catch (error) {
                      captureException(error)
                      toast.error(
                        tt.student_assignment.solo_room_creation_error()
                      )
                      return
                    }
                    showDialog(({ remove: removeQuizModal }) => {
                      return (
                        <QuizModal
                          cubit={cubit}
                          closeModal={() => {
                            removeQuizModal()
                          }}
                        />
                      )
                    })
                    removeStartQuizModal()
                  }}
                />
              )
            })
          }
          case ValidLibraryObjectActionState.startSession: {
            if (cubit.assignment.isSolo) {
              // only act when the central mapping agrees — this excludes
              // expired/canceled/unpaid/not-yet-available assignments,
              // instructor-led pending, and locked future schedules, so a
              // stale deep link can't create a room
              if (
                cubit.libraryObject.getActionState({
                  hasOutstandingPeerReview: false,
                }) !== ValidLibraryObjectActionState.startSession
              )
                return
              if (!cubit.allPreWorkQuestionsAnswered) return
              const startSolo = async () => {
                try {
                  await cubit.startSoloSession()
                } catch (error) {
                  captureException(error)
                  toast.error(tt.student_assignment.solo_room_creation_error())
                  return
                }
                showWalkthroughDialogIfNecessary(() => {
                  cubit.logEvent('room_join')
                  cubit.setJoiningSession()
                }, cubit.roomStateId ?? undefined)
              }
              void startSolo()
              return
            }

            const {
              libraryObject: { libraryObjectState },
            } = cubit

            const canStart =
              (libraryObjectState === LibraryObjectState.readyToStart ||
                (libraryObjectState === LibraryObjectState.live &&
                  !cubit.didRoomStart)) &&
              cubit.allPreWorkQuestionsAnswered &&
              cubit.roomState.isScheduled &&
              cubit.roomState.isNotEmpty &&
              // blocks starting when role assignment OR the assignment's minimum
              // group size requirements are not met (this closes the Homepage
              // "Start Session" CTA bypass)
              !cubit.roleIssueForRoomState &&
              !cubit.minGroupSizeIssueForRoomState

            if (!canStart) return
            return showWalkthroughDialogIfNecessary(() => {
              cubit.logEvent('room_join')
              cubit.setJoiningSession()
            })
          }
          case ValidLibraryObjectActionState.viewResults: {
            const isCompleted =
              cubit.roomState.getRoomStateStatus(
                cubit.assignment.expiresAt,
                cubit.section.data.sectionState,
                cubit.slideDeck.data.slideDeckSlideCount,
                cubit.assignment.data.assignmentState,
                cubit.roomGraded.value
              ) === RoomStateStatus.completed

            const hasRoomStartedAt =
              cubit.roomState.data.roomStartedAt !== undefined
            const canShowNewResults =
              isCompleted && hasRoomStartedAt && Boolean(cubit.roomStateId)
            if (cubit.roomStateSummary.hasData || canShowNewResults) {
              showDialog(() => {
                if (canShowNewResults)
                  return <MeetingResultsDialog roomId={cubit.roomStateId!} />
                return (
                  <SummaryDialog
                    summary={cubit.roomStateSummary.data}
                    roomStateName={cubit.roomState.roomStateName}
                    roomStateActiveSlideChangedAt={
                      cubit.roomState.data.activeSlideChangedAt || null
                    }
                  />
                )
              })
            }
            return
          }

          case ValidLibraryObjectActionState.loading: // no op
          case ValidLibraryObjectActionState.enroll: // pops from reaction
          case ValidLibraryObjectActionState.joinSession: // pops from reaction
          case ValidLibraryObjectActionState.suspended: // same reaction as joinSession
          case ValidLibraryObjectActionState.availableOn: // no op
          case ValidLibraryObjectActionState.sessionScheduled: // no op
          case ValidLibraryObjectActionState.pendingGrouping: // no op
          case ValidLibraryObjectActionState.pendingScheduling: // no op
          case ValidLibraryObjectActionState.finishEndOfSessionQuiz: // renders a button on the assignment page, no dialog to pop
          case ValidLibraryObjectActionState.completePeerReviews: // navigates to assignment page, handled by component
          case ValidLibraryObjectActionState.experienceExpired: // nop
          case ValidLibraryObjectActionState.canceled: // no op
            return
          default: {
            const _exhaustiveCheck: never = action // for compile time switch case exhaustiveness
            throw new Error(`Unhandled action: ${_exhaustiveCheck}`)
          }
        }
      },
      [cubit, popDialog, showDialog, showWalkthroughDialogIfNecessary, tt]
    )

    useEffect(() => {
      const params = store.router.params || {}

      // Detect a situation where the params have changed and they
      // do not match the cubit - that means the student assignment page
      // has changed
      if (params.sectionId?.toString() !== cubit.sectionId) return
      if (params.assignmentId?.toString() !== cubit.assignmentId) return
      // ensure both have same empty state for comparison
      const paramsRoomStateId = params.roomStateId?.toString() || undefined
      const cubitRoomStateId = cubit.roomStateId
      if (paramsRoomStateId !== cubitRoomStateId) return

      const isReady =
        cubit.isReady &&
        cubit.repository.featureFlags.isLoaded &&
        cubit.roomStates.isLoaded &&
        // solo can act without a room, but only once the quiz data is known
        // (allPreWorkQuestionsAnswered is vacuously true while unloaded)
        (!cubit.assignment.isSolo || cubit.hasQuizData) &&
        (!cubit.roomStateId ||
          (cubit.roomState.isLoaded &&
            cubit.hasQuizData &&
            cubit.slideDeck.isLoaded &&
            cubit.assignment.isLoaded &&
            cubit.roles.isLoaded &&
            cubit.roomGraded.isLoaded))
      if (!isReady) return
      const actionStr = (store.router.queryParams ?? {})['action']

      // once we've attempted to grab remove the param from the url
      runInAction(() => {
        if (store.router.queryParams && 'action' in store.router.queryParams) {
          // Ok, we need to do some manual work, because we want to do a replaceState
          const queryParams = new URLSearchParams(window.location.search)
          const stringifiedQueryParams = queryParams.toString()
          const newRelativePathQuery =
            window.location.pathname +
            (stringifiedQueryParams !== '' ? `?${stringifiedQueryParams}` : '')
          window.history.replaceState(null, '', newRelativePathQuery)
          store.updateQueryParams({ action: undefined })
        }
      })

      if (typeof actionStr !== 'string' || !actionStr) return

      if (actionStr === 'chat' || actionStr === 'takeQuiz') {
        runAction(actionStr)
        return
      }

      const action = ValidLibraryObjectActionState[
        actionStr as keyof typeof ValidLibraryObjectActionState
      ] as ValidLibraryObjectActionState | undefined
      if (!action) return
      runAction(action)

      // action is defined at this point, attempt to perform the action
    }, [
      store,
      runAction,
      cubit.roomStateId,
      cubit.sectionId,
      cubit.assignmentId,
      cubit.isReady,
      store.router.params,
      store.router.queryParams,
      cubit.repository.featureFlags.isLoaded,
      cubit.roomState.isLoaded,
      cubit.hasQuizData,
      cubit.roomStates.isLoaded,
      cubit.slideDeck,
      cubit.assignment,
      cubit.roomGraded,
      cubit.roles.isLoaded,
    ])

    return null
  }
)
